import { phone, cep, cpf } from "src/framework/commons/masks";
import { POSICAO_MODULO, PROPOSTA_OPTIONS } from "../constants";
import { FormProjetoOffGridType, ListagemItem, SolucaoCalculadaOffGrid, EstruturaListagemResponse, Bateria, ModuloPorRegiaoListagemResponse, Fileira, TiposBaterias } from "../types";
import { ProjetoComponente, ProjetoOutput, SolucaoSugeridaOffGrid } from "./types";
import { Conta, Estado, Regiao } from "src/types";
import { cnpj } from "src/framework/commons/masks";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { TENSAO_SAIDA_OPTIONS } from "../offgrid/FormClienteOffGrid/containers/TensaoSaidaInput";
import { calcularAcessorios } from "../dimensionados_offgrid/utils/calcularAcessorios";
import { buscarEstruturasPorModulo } from "../dimensionados_offgrid/utils/buscarEstruturasPorModulo";
import { ComponenteProvider } from "src/providers/estoque/ComponenteProvider";
import { ProjetosOffGridProvider } from "src/providers/ProjetosOffGridProvider";
import { calcularEstruturas } from "../dimensionados_offgrid/utils/calcularEstruturas";
import { elSemEstrutura } from "../dimensionados_offgrid/containers/modalEditarEstruturas";
import { ProjetosProvider } from "src/providers/ProjetosProvider";
import { apiNoLoading } from "src/framework/providers/api";
import { calcularPotenciaEquipamentosOffGrid } from "../utils/calcularPotenciaEquipamentosOffGrid";
import { TIPOS_BATERIAS_OPTIONS } from "../offgrid/FormClienteOffGrid/containers/TipoBateriaInput";
import { oldEstruturas$ } from "src/store/vendas/projetos/offgrid.store";
import { ID_MODULO } from "src/components/componentes/types";
import { safeGeocodeByAddress } from "src/framework/commons/MapsUtil";

interface EstruturasOutput {
  ativos: EstruturaListagemResponse[];
  todos: EstruturaListagemResponse[];
  retrato: EstruturaListagemResponse[];
  paisagem: EstruturaListagemResponse[];
}

interface Props {
  estados: Estado[]
  regioes: any[];
  estadosServico: any[];
  clonarProjeto: boolean;
}

const componenteProvider = new ComponenteProvider()
const projetosOffGridProvider = new ProjetosOffGridProvider();
const projetosProvider = new ProjetosProvider();

export interface ProjetoOutputOffGrid extends Omit<ProjetoOutput, "solucao_sugerida" | "componentes"> {
  equipamentos: Array<{
    id?: number
    nome: string
    quantidade: number
    potencia: number
    horas_uso: number
    consumo_diaria: number
  }>
  solucao_sugerida: SolucaoSugeridaOffGrid
  componentes: ProjetoComponente[]
}

export class ProjetoHelperOffGrid {
  public moduloSelecionado: ProjetoOutputOffGrid["componentes"][0] | null = null;
  public regioesProjeto: number[] = [];
  public solucaoCalculada: SolucaoCalculadaOffGrid | null = null;
  public estruturasListagem: EstruturasOutput = {
    ativos: [],
    paisagem: [],
    retrato: [],
    todos: [],
  }
  public bateriasListagem: Bateria[] = [];
  public modulosListagem: ModuloPorRegiaoListagemResponse[] = [];
  public acessoriosCalculados: ListagemItem[] = [];
  public estruturasCalculadas: ListagemItem[] = [];
  public valorMinimoServico: { valor_minimo_servico: number } | null = null;
  
  constructor(private data: ProjetoOutputOffGrid, private readonly props: Props){
    this.data = this.normalizar()
    this.definirModuloSelecionado();
    this.definirRegioes();
  }
  
  definirModuloSelecionado = () => {
    const moduloSelecionado = this.data.componentes.find(item => item.componente.categoria_componentes_id === ID_MODULO) || null;
    if (!moduloSelecionado) {
      console.error("Modulo selecionado não encontrado")
    }

    this.moduloSelecionado = moduloSelecionado;
  }
  
  calcularValorServico = async () => {
    const valorMinimoServicoResult = await projetosProvider.calcularValorMinimoServico(this.data.solucao_sugerida.geracao_necessaria_kw, apiNoLoading);
    this.valorMinimoServico = valorMinimoServicoResult.data;
  }

  calcularEstruturas = async () => {
    const fileirasComEstrutura = this.data.solucao_sugerida.fileiras.filter(item => {
      return item.estrutura_id !== 0;
    })

    if (!fileirasComEstrutura.length) return;

    const result = await calcularEstruturas({
      controladores: this.solucaoCalculada?.controladores || [],
      fileiras: fileirasComEstrutura.map(item => ({
        fileiras: item.fileiras_iguais,
        posicao_modulo: POSICAO_MODULO.find(posItem => item.posicao === posItem.value) || null,
        quantidade_modulos: item.modulos_sequencia,
        tipo_estrutura: { value: item.estrutura_id, image: '', text: '' },
      })),
      inversor: '',
      inversores: this.solucaoCalculada?.inversores || [],
      moduloId: this.moduloSelecionado?.componente.id || 0,
      potencia: this.data.solucao_sugerida.geracao_necessaria_kw,
      regioes: this.regioesProjeto,
    })
    oldEstruturas$.next(result);
    this.estruturasCalculadas = result.map(item => {
      const componenteProj = this.data.componentes.find(comp => comp.componente.id === item.componente_id);

      return {
        ...item,
        quantidade: componenteProj?.quantidade ?? item.quantidade ?? 0,
      }
    });
  }

  buscarBaterias = async () => {
    const result = await componenteProvider.buscarBateriasPorRegiao(
      this.regioesProjeto, 
      "off",
    )

    this.bateriasListagem = result.data || [];
  }

  calcularAcessorios = async () => {
    const result = await calcularAcessorios({
      moduloId: this.moduloSelecionado?.componente.id || 0,
      regioes: this.regioesProjeto,
      inversores: this.solucaoCalculada?.inversores || [],
      controladores: this.solucaoCalculada?.controladores || [],
      quantidade_modulos: this.moduloSelecionado?.quantidade || 0,
    })

    this.acessoriosCalculados = result.data.map(item => {
      const componenteProj = this.data.componentes.find(comp => comp.componente.id === item.componente_id);

      return {
        ...item,
        quantidade: componenteProj?.quantidade ?? item.quantidade ?? 0,
      }
    })
  }

  calcularSolucao = async () => {
    const solucaoCalculada = await projetosOffGridProvider.calcularSolucao({
      autonomia_bateria: Number(this.data.solucao_sugerida.autonomia_banco_baterias || 0),
      regioes: this.regioesProjeto,
      potencia_fotovoltaica: this.data.solucao_sugerida.potencia_fotovoltaica,
      consumo_total_diario: this.data.solucao_sugerida.total_consumo_diario_w,
      quantidade_modulos: this.moduloSelecionado?.quantidade || 0,
      tensao_saida: this.data.solucao_sugerida.tensao_saida || '',
      tipo_bateria: this.data.solucao_sugerida.tipo_bateria ? this.data.solucao_sugerida.tipo_bateria as TiposBaterias : null,
      potencia_equipamentos: calcularPotenciaEquipamentosOffGrid(this.data.equipamentos || [])
    })

    if (solucaoCalculada.data) {
      if (solucaoCalculada.data.modulo?.alterouQuantidade && !!this.moduloSelecionado) {
        this.moduloSelecionado.quantidade = solucaoCalculada.data.modulo.quantidade || this.moduloSelecionado.quantidade || 0
      }

      this.solucaoCalculada = {
        baterias: solucaoCalculada.data.baterias.map(item => {
          const componenteProj = this.data.componentes.find(comp => comp.componente.id === item.id);
          return {
            ...item,
            quantidade: componenteProj?.quantidade || item?.quantidade || 0,
          }
        }),
        controladores: solucaoCalculada.data.controladores.map(item => {
          const componenteProj = this.data.componentes.find(comp => comp.componente.id === item.id);
          return {
            ...item,
            quantidade: componenteProj?.quantidade || item.quantidade || 0,
          }
        }),
        inversores: solucaoCalculada.data.inversores.map(item => {
          const componenteProj = this.data.componentes.find(comp => comp.componente.id === item.id);
          return {
            ...item,
            quantidade: componenteProj?.quantidade || item.quantidade || 0,
          }
        }),
        modulo: solucaoCalculada.data.modulo
      }
    }
  }
  
  definirRegioes() {
    this.regioesProjeto = this.data.solucao_sugerida.regiao_id || [];
  }
  
  buscarModulos = async () => {
    const result = await componenteProvider.buscarModulosPorRegiao(
      this.regioesProjeto.map(String), 
      Number(this.data.solucao_sugerida.geracao_necessaria_kw),
      undefined,
      undefined,
      undefined,
      undefined,
      "off",
    )

    this.modulosListagem = result
  }
  
  buscarEstruturas = async () => {
    const result = await buscarEstruturasPorModulo({
      moduloId: this.moduloSelecionado?.componente.id || 0,
      potencia: this.data.solucao_sugerida.geracao_necessaria_kw || 0,
      regioesId: this.data.solucao_sugerida.regiao_id || [],
    })

    this.estruturasListagem = result || [];
  }

  getLatLngFromAddress = async (): Promise<{ lat: number; lng: number }> => {
    const { logradouro, numero, bairro, cep }  = this.data.endereco;
    const { cidade, estado_id } = this.data
    const endereco = `${logradouro || ''}, ${numero || ''}, ${bairro || ''}, ${cidade?.nome || ''}, ${estado_id}, ${cep}`
    
    try {
      const res = await safeGeocodeByAddress(endereco)
      if (!res?.[0]) return { lat: 0, lng: 0 }
      return getLatLng(res[0])
    } catch (error) {
      return {
        lat: 0,
        lng: 0,
      }
    }
    
  }

  normalizar = (): ProjetoOutputOffGrid => {
    const maskedPhone = phone({ target: { value: this.data.telefone_proposta || '' } })?.target?.value || ''
    const maskedCep = cep({ target: { value: this.data.endereco?.cep || '' } })?.target?.value || ''
    const maskedCpfCnpj = this.formatarCpfOuCnpj(this.data.cpf_cnpj_proposta)

    return {
      ...this.data,
      telefone_proposta: maskedPhone,
      cpf_cnpj_proposta: maskedCpfCnpj,
      endereco: {
        ...(this.data.endereco || {}),
        cep: maskedCep,
      },
    };
  }

  formatarCpfOuCnpj = (value: string) => {
    if (!value) return '';
    const formatValue = value?.replace(/[^a-zA-Z0-9]/g, '')
    return formatValue.length === 14 ? cnpj({ target: { value: formatValue } })?.target?.value || '' : cpf({ target: { value: formatValue } })?.target?.value || ''
  }

  buscarEstado = (): { id: string; nome: string } | undefined => {
    if (!this.data.estado_id) return undefined;
    const estado = this.props.estados?.find((item) => item.id === this.data.estado_id)
    return estado ? { id: estado.id, nome: estado.nome } : undefined
  }

  mostrarOpcaoServico = () => {
    if (!this.data?.id) return true;
    const reg = this.props.regioes || []
    const estadosServico = this.props.estadosServico || []
    const estadosPermitemServico: string[] = estadosServico.map((item: any) => item.regiao_id)
    const currentRegioes: string[] = reg.filter((item: Regiao) => {
      if (!item.estados || item.estados.length === 0) return false;
      const ids = item.estados.map((estado: any) => estado.estado_id)
      return ids.includes(this.data.estado_id)
    }).map((item: any) => item.id)
    return estadosPermitemServico.some(item => {
      if (currentRegioes.includes(item)) return true;
      return false;
    })
  }

  getRegioes = () => {
    if (!this.data.estado_id) return []
    return this.props.regioes?.filter((regiao: any) => {
      const estadosList = regiao.estados || []

      const estados = estadosList.map((item: any) => item.estado_id)
      return estados.includes(this.data.estado_id)
    }).map(item => item.id)
  }

  formatarEquipamentos = (clonarProjeto: boolean): FormProjetoOffGridType["equipamentos"] => {
    return this.data.equipamentos.map(item => ({
      id: clonarProjeto ? undefined : item.id,
      consumo_diaria: item.consumo_diaria,
      horas_uso: item.horas_uso,
      nome: item.nome,
      potencia: item.potencia,
      quantidade: item.quantidade,
    }))
  }

  get estruturaSelecionada() {
    const fileiras = this.fileiras.fileiras;
    const primeiraFileira = fileiras?.[0];
    if (!primeiraFileira) return null;
    if (primeiraFileira.tipo_estrutura?.value === 0) return elSemEstrutura;
    return this.estruturasListagem.ativos.find(item => item.id === primeiraFileira.tipo_estrutura?.value && item.estruturas_grupo_id === primeiraFileira.tipo_estrutura?.estruturas_grupo_id);
  }

  get fileiras(): { fileiras: Fileira[]; fileirasInvalidas: number[] } {
    const fileirasInvalidas: number [] = [];

    const fileiras = this.data.solucao_sugerida.fileiras.map((fileira, index) => {
      let statusFileira: "sem_estrutura" | "nao_encontrado" | 'ok' = 'ok'
      const posicaoFileira = fileira.posicao || null;
      const estruturaListagem = posicaoFileira === 'retrato' ? this.estruturasListagem.retrato : this.estruturasListagem.paisagem;
      
      if (fileira.estrutura_id === 0) {
        statusFileira = "sem_estrutura"
      }

      const estruturaSelecionada = statusFileira === 'sem_estrutura' ? null : estruturaListagem.find(
        estrutura => estrutura.id === fileira.estrutura_id && 
        estrutura?.estruturas_grupo_id === fileira?.estruturas_grupo_id && 
        estrutura?.grupos?.length > 0
      );
      
      if (estruturaSelecionada === undefined) {
        statusFileira = "nao_encontrado"
        fileirasInvalidas.push(index)
      }

      const findGrupo = estruturaSelecionada?.grupos?.find(g => g.id === estruturaSelecionada.estruturas_grupo_id) || estruturaSelecionada?.grupos?.[0];
      let itemImageUrl = findGrupo?.url_imagem || estruturaSelecionada?.url_imagem || elSemEstrutura.url_imagem;

      let estruturaLocal: any = elSemEstrutura;
      
      if (statusFileira === 'ok' && estruturaSelecionada) {
        estruturaLocal = {
          image: itemImageUrl,
          text: estruturaSelecionada?.nome,
          value: estruturaSelecionada?.id,
          estruturas_grupo_id: estruturaSelecionada?.estruturas_grupo_id,
        }
      }

      if (statusFileira === 'nao_encontrado') {
        estruturaLocal = null
      }

      return {
        fileiras: fileira.fileiras_iguais,
        posicao_modulo: POSICAO_MODULO.find(posicao => posicao.value === fileira.posicao) || null,
        quantidade_modulos: fileira.modulos_sequencia,
        tipo_estrutura: estruturaLocal
      }
    })

    return {
      fileiras,
      fileirasInvalidas,
    }
  }

  formatar = async (): Promise<(FormProjetoOffGridType & { fileiras_invalidas?: number[] })> => {
    await Promise.all([
      this.buscarEstruturas(),
      this.buscarBaterias(),
      this.buscarModulos(),
      this.calcularSolucao(),
      this.calcularValorServico(),
    ])

    await Promise.all([
      this.calcularAcessorios(),
      this.calcularEstruturas(),
    ])

    const latLngFromAddress = await this.getLatLngFromAddress()
    const tipoProposta = PROPOSTA_OPTIONS.find(item => item.value === (!!this.data.valor_servico ? 'gerador-com-instalacao' : 'gerador-sem-instalacao')) || null

    const fileiras = this.fileiras;
    const estruturaSelecionada = this.estruturaSelecionada;

    return {
      id: this.data.id,
      acessorios: [],
      tipo: "dimensionado",
      tipo_proposta: tipoProposta,
      titulo: this.data.titulo,
      todas_estruturas: [],
      total_consumo_diario_kw: this.data.solucao_sugerida.total_consumo_diario_kw,
      total_consumo_diario_w: this.data.solucao_sugerida.total_consumo_diario_w,
      valor_minimo_servico: 0,
      valor: this.data.valor || 0,
      valor_servico: this.data.valor_servico || 0,
      valor_servico_pis_cofins: this.data.valor_servico_pis_cofins || 0,
      codigo_servico: this.data.codigo_servico,
      estado: this.buscarEstado(),
      estado_original: this.buscarEstado(),
      estado_id: this.data.estado_id,
      ev: (this.data.ev as unknown) as Conta,
      ev_id: this.data.ev?.id,
      revenda: (this.data.revenda as unknown) as Conta,
      justificativa_valor_servico: this.data.justificativa_valor_servico || null,
      autonomia_banco_bateria: this.data.solucao_sugerida?.autonomia_banco_baterias,
      revenda_id: this.data.revenda?.id,
      regioes: this.getRegioes(),
      latitude: latLngFromAddress.lat,
      longitude: latLngFromAddress.lng,
      analista: (this.data.analista as unknown) as Conta,
      analista_id: this.data.analista_id,
      assistente_id: this.data.assistente_id,
      bairro: this.data.endereco?.bairro || '',
      baterias: [],
      cep: this.data.endereco.cep,
      cep_encontrado: !!this.data.endereco.cep,
      cidade: this.data.cidade ? { estado_id: this.data.cidade.estado_id, cidade_codigo: this.data.cidade.cidade_codigo, id: this.data.cidade.id, nome: this.data.cidade.nome } : null,
      cidade_id: this.data.cidade?.id,
      cliente_proposta: this.data.cliente_proposta,
      clonar_projeto: this.props.clonarProjeto,
      codigo_venda: this.data.codigo_venda,
      cpf_cnpj_proposta: this.data.cpf_cnpj_proposta,
      distribuidor: this.data.distribuidor ? {
        cnpj_cpf: this.data.distribuidor.cnpj_cpf,
        fantasia: this.data.distribuidor.fantasia,
        id: this.data.distribuidor.id,
        nome: this.data.distribuidor.nome
      } : null,
      distribuidor_id: this.data.distribuidor?.id,
      email_proposta: this.data.email_proposta,
      endereco_proposta: this.data.endereco?.logradouro || '',
      equipamentos: this.formatarEquipamentos(this.props.clonarProjeto),
      estrutura_selecionada: estruturaSelecionada|| null,
      estruturas: [],
      estruturas_calculadas: [],
      fileiras: fileiras.fileiras,
      fileiras_invalidas: fileiras.fileirasInvalidas,
      geracao_necessaria_kw: this.data.solucao_sugerida.geracao_necessaria_kw || 0,
      geracao_necessaria_w: this.data.solucao_sugerida.geracao_necessaria_w || 0,
      inversores: [],
      isEdit: true,
      modulo_selecionado: null,
      modulos: [],
      numero: this.data.endereco?.numero || '',
      potencia_fotovoltaica: this.data.solucao_sugerida.potencia_fotovoltaica || 0,
      recebe_servico: this.mostrarOpcaoServico(),
      resultado_calculos: null,
      segmento: "distribuicao_offgrid",
      servico: this.data.servico,
      solucao_calculada: {
        baterias: [],
        controladores: [],
        inversores: [],
        modulo: null,
      },
      supervisor_id: this.data.supervisor_id,
      telefone_proposta: this.data.telefone_proposta,
      tensao_saida: this.data.solucao_sugerida && this.data.solucao_sugerida.tensao_saida ? (TENSAO_SAIDA_OPTIONS.find(item => item.id === this.data.solucao_sugerida.tensao_saida) || null) : null,
      old_componentes_edicao: [],
      old_estado_id: '',
      tipo_bateria: this.data.solucao_sugerida?.tipo_bateria ? (TIPOS_BATERIAS_OPTIONS.find(item => item.id === this.data.solucao_sugerida.tipo_bateria) || null) : null,
      listagem_baterias_status: false,
    }
  }
}